import { z } from 'zod';

import { validatePhoneByCountry } from '../constants/countryCodes';

/**
 * Rejects strings containing asterisks (masked content).
 */
const noAsterisk = (schema: z.ZodString) =>
  schema.refine(
    (val) => !val.includes('*'),
    'validation.contains_masked_content',
  );

/**
 * Password policy (matches mobile registration requirements):
 * - Minimum 9 characters
 * - At least 1 lowercase letter
 * - At least 1 uppercase letter
 * - At least 1 number
 * - At least 1 special character
 */
const passwordSchema = z
  .string()
  .min(9, 'validation.password_min_length')
  .regex(/[a-z]/, 'validation.password_needs_lowercase')
  .regex(/[A-Z]/, 'validation.password_needs_uppercase')
  .regex(/\d/, 'validation.password_needs_number')
  .regex(/[@$!%*?&]/, 'validation.password_needs_special');

export const createAdminSchema = z
  .object({
    username: noAsterisk(
      z
        .string()
        .min(6, 'validation.username_min_length')
        .max(50, 'validation.username_max_length')
        .regex(/^[a-zA-Z0-9]+$/, 'validation.username_alphanumeric'),
    ),
    displayName: noAsterisk(
      z
        .string()
        .min(2, 'validation.display_name_min_length')
        .max(100, 'validation.display_name_max_length'),
    ),
    password: passwordSchema,
    email: z
      .string()
      .email('validation.email_invalid')
      .optional()
      .or(z.literal('')),
    countryCode: z
      .string()
      .regex(/^[0-9]{1,5}$/, 'validation.country_code_format')
      .optional()
      .or(z.literal('')),
    mobileNumber: z
      .string()
      .regex(/^[0-9]{0,15}$/, 'validation.mobile_format')
      .optional()
      .or(z.literal('')),
  })
  .refine(
    (data) => {
      if (!data.mobileNumber) return true;

      return validatePhoneByCountry(
        data.countryCode || '86',
        data.mobileNumber,
      );
    },
    {
      message: 'validation.mobile_invalid_for_country',
      path: ['mobileNumber'],
    },
  );

export const updateAdminSchema = z
  .object({
    displayName: z
      .string()
      .min(2, 'validation.display_name_min_length')
      .max(100, 'validation.display_name_max_length')
      .refine((val) => !val.includes('*'), 'validation.contains_masked_content')
      .optional(),
    password: passwordSchema.or(z.literal('')).optional(),
    email: z
      .string()
      .email('validation.email_invalid')
      .optional()
      .or(z.literal('')),
    countryCode: z
      .string()
      .regex(/^[0-9]{1,5}$/, 'validation.country_code_format')
      .optional()
      .or(z.literal('')),
    mobileNumber: z
      .string()
      .regex(/^[0-9]{0,15}$/, 'validation.mobile_format')
      .optional()
      .or(z.literal('')),
  })
  .refine(
    (data) => {
      if (!data.mobileNumber) return true;

      return validatePhoneByCountry(
        data.countryCode || '86',
        data.mobileNumber,
      );
    },
    {
      message: 'validation.mobile_invalid_for_country',
      path: ['mobileNumber'],
    },
  );

export type ICreateAdminFormData = z.infer<typeof createAdminSchema>;
export type IUpdateAdminFormData = z.infer<typeof updateAdminSchema>;
